import {
  ChatStatus,
  fetchProjectChat,
  fetchProjectChatFile,
  useSDK,
  type LittlebirdSDK,
  type Message,
  type SharedChatDetail,
  type SharedContentFile,
  type Thread,
} from '@littlebird/sdk';
import { useSharedProjectDetail } from '@littlebird/ui-utils';
import { useCallback } from 'react';

import { convertChatHistoryToMessages } from '@/features/chat/shared-chat/shared-chat.hooks';
import { logError } from '@/lib/errorLogging';

export interface ProjectSharedChatDetail {
  title: string;
  authorEmail: string | null;
  messages: Message[];
  thread: Thread;
}

function hydrateFiles(
  sdk: LittlebirdSDK,
  projectId: string,
  chatId: string,
  files: SharedContentFile[]
) {
  for (const meta of files) {
    sdk.stores.files.addFile({
      id: meta.id,
      filename: meta.filename,
      contentType: meta.contentType,
      size: meta.size,
      width: meta.width ?? undefined,
      height: meta.height ?? undefined,
    });
    sdk.stores.files.registerFileUrl(meta.id, {
      url: meta.url,
      expiresIn: meta.expiresIn,
      refresh: async () => {
        const renewed = await fetchProjectChatFile(
          sdk,
          projectId,
          chatId,
          meta.id
        );
        return { url: renewed.url, expiresIn: renewed.expiresIn };
      },
    });
  }
}

async function toDetail(
  sdk: LittlebirdSDK,
  projectId: string,
  chat: SharedChatDetail
): Promise<ProjectSharedChatDetail> {
  const messages = await convertChatHistoryToMessages(
    chat.messages,
    chat.chatId
  );
  hydrateFiles(sdk, projectId, chat.chatId, chat.files);
  return {
    title: chat.title,
    authorEmail: chat.authorEmail,
    messages,
    thread: {
      id: chat.chatId,
      title: chat.title,
      starred: false,
      status: ChatStatus.Done,
    } as unknown as Thread,
  };
}

export function useProjectSharedChatDetail(projectId: string, chatId: string) {
  const sdk = useSDK();
  const fetchDetail = useCallback(
    () =>
      fetchProjectChat(sdk, projectId, chatId).then((chat) =>
        toDetail(sdk, projectId, chat)
      ),
    [chatId, projectId, sdk]
  );
  const onError = useCallback(
    (error: unknown) => {
      logError(error, 'Failed to load shared project chat', {
        context: 'useProjectSharedChatDetail',
        metadata: { projectId, chatId },
      });
    },
    [chatId, projectId]
  );
  return useSharedProjectDetail({
    fetchDetail,
    kind: 'chat',
    projectId,
    contentId: chatId,
    onError,
  });
}
